import { useState, useEffect, SyntheticEvent } from 'react';
import {
  Grid, Card, Button, Divider, CardContent, MenuItem,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import CardActions from '@mui/material/CardActions';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useNavigate, Link } from 'react-router-dom';
import Title from '../../../components/Title';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import axios from "axios";
import { Role } from '../../../models/role';
import { Collect } from '../../../models/collect';
import { Office } from '../../../models/office';
import SelectMultipleCollectsByOffice from '../../../components/filter/SelectMultipleCollectsByOffice';
import { useDrawer } from '../../../components/context/DrawerContext';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const UserAdd: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const navigate = useNavigate();

  const [firstname, setFirst_name] = useState('');
  const [lastname, setLast_name] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password_confirm, setPassword_confirm] = useState('');
  const [role_id, setRole_id] = useState('');
  const [username, setUsername] = useState('');
  const [idnumber, setIdnumber] = useState('');
  const [offices, setOffices] = useState<Office[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [officeCollects, setOfficeCollects] = useState<{ office_id: string; selectedCollects: string[] }[]>([]);
  const [collectsByOffice, setCollectsByOffice] = useState<Record<string, Collect[]>>({});
  const { drawerOpen } = useDrawer();

  useEffect(() => {
    (async () => {
      try {
        showLoading();
        const [resRoles, resOffices, resCollects] = await Promise.all([
          axios.get('roles'),
          axios.get('alloffices'),
          axios.get('allcollects')
        ]);

        setRoles(resRoles.data);
        setOffices(resOffices.data.data);

        const grouped: Record<string, Collect[]> = {};
        resCollects.data.data.forEach((collect: Collect) => {
          const officeId = collect.office.id.toString();
          if (!grouped[officeId]) grouped[officeId] = [];
          grouped[officeId].push(collect);
        });

        setCollectsByOffice(grouped);
        hideLoading();
      } catch (error) {
        console.error("Error cargando datos:", error);
        hideLoading();
      }
    })();
  }, []);

  const handleOfficeCollectsChange = (data: { office_id: string; selectedCollects: string[] }[]) => {
    setOfficeCollects(data);
  };
  const isFormIncomplete = officeCollects.some(
    (entry) => !entry.office_id || entry.selectedCollects.length === 0
  );
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!firstname || !lastname || !username || !password || !password_confirm || !role_id || officeCollects.length === 0) {
      showAlert('Tiene campos pendientes por diligenciar', 'warning');
      return;
    }

    if (password !== password_confirm) {
      showAlert('Las contraseñas no coinciden', 'warning');
      return;
    }

    showLoading();

    const formData = {
      firstname,
      lastname,
      idnumber,
      username,
      password,
      password_confirm,
      role_id,
      type_user_id: 2,
      offices: officeCollects.map(item => ({
        office_id: parseInt(item.office_id),
        collect_id: item.selectedCollects.map(id => parseInt(id))
      }))
    };

    try {
      await axios.post('users', formData);
      showAlert('Usuario creado correctamente!', 'success');
      navigate('/administrar/usuarios');
    } catch (error) {
      console.error('Error creando el usuario:', error);
      showAlert('Error creando el usuario', 'error');
    } finally {
      hideLoading();
    }
  };

  return (
    <HeaderResponsive>
      <StickyListToolbar>
        <Title>Crear Usuario</Title>
      </StickyListToolbar>
      <Card>
        <form onSubmit={submit}>
          <CardContent
            sx={{
              width: (theme) => stickyListRegionWidth(theme, drawerOpen),
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Cédula" onChange={e => setIdnumber(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Usuario" onChange={e => setUsername(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Nombres" onChange={e => setFirst_name(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Apellidos" onChange={e => setLast_name(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Contraseña" type="password" onChange={e => setPassword(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Confirmar Contraseña" type="password" onChange={e => setPassword_confirm(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="Rol">Seleccionar Perfil</InputLabel>
                  <Select labelId="Rol" size='small' value={role_id} onChange={e => setRole_id(e.target.value)}>
                    {roles.map((role: Role) => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <SelectMultipleCollectsByOffice
              offices={offices}
              collectsByOffice={collectsByOffice}
              onChange={handleOfficeCollectsChange}
              initialSelections={[]} // <- evita errores al crear
            />

            <Divider sx={{ margin: 2 }} />
            <CardActions sx={{ margin: 2 }} >
              <Button size='small' type="submit" sx={{ mr: 2 }} variant="contained" disabled={isFormIncomplete} >Guardar</Button>
              <Link to="/administrar/usuarios"><Button size='small' color="secondary" variant="outlined">Cancelar</Button></Link>
            </CardActions>
            <Divider sx={{ margin: 2 }} />
          </CardContent>
        </form>
      </Card>
    </HeaderResponsive>
  );
};

export default UserAdd;
